import React from "react";
import { Clock, TrendingUp, ArrowDown, ArrowUp } from "lucide-react";

const HOUR_LABEL = (h) => {
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
};

const CAT_LABEL = (c) => (c || "").replaceAll("_", " ").toUpperCase();

/**
 * Two side-by-side insight cards on the profile:
 *  - Reading Trajectory: top category this month vs previous
 *  - Time-of-Day Profile: peak hour + persona tag + tiny 24h sparkline
 */
export default function InsightsRow({ trajectory, timeOfDay }) {
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <TrajectoryCard trajectory={trajectory} />
      <TimeOfDayCard timeOfDay={timeOfDay} />
    </div>
  );
}

function TrajectoryCard({ trajectory }) {
  const cur = trajectory?.current;
  const prev = trajectory?.previous;
  let arrow = null;
  let arrowColor = "#94a3b8";
  if (cur && prev) {
    if (cur.category === prev.category) {
      arrow = <span className="text-zinc-500 font-mono text-xs">→ holding steady</span>;
    } else {
      arrow = (
        <span className="inline-flex items-center gap-1 font-mono text-xs text-[#a78bfa]">
          <ArrowUp size={11} /> shifted from {CAT_LABEL(prev.category)}
        </span>
      );
      arrowColor = "#a78bfa";
    }
  }
  return (
    <div data-testid="profile-trajectory" className="border border-[#a78bfa]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#a78bfa]/[0.10] to-[#a78bfa]/[0.02] p-5">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={13} className="text-[#a78bfa]" />
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">
          // READING TRAJECTORY
        </div>
      </div>
      {!cur ? (
        <div className="font-mono text-xs text-zinc-500 mt-3">
          Start reading dossiers to chart your topic trajectory.
        </div>
      ) : (
        <>
          <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 mb-1 mt-2">
            This month
          </div>
          <div className="font-heading text-white text-xl truncate" style={{ color: arrowColor }}>
            {CAT_LABEL(cur.category)}
          </div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mt-1">
            {cur.count} interactions
          </div>
          <div className="mt-3 pt-3 border-t border-white/5">
            {arrow}
          </div>
        </>
      )}
    </div>
  );
}

function TimeOfDayCard({ timeOfDay }) {
  if (!timeOfDay) {
    return (
      <div className="border border-[#a78bfa]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#a78bfa]/[0.10] to-[#a78bfa]/[0.02] p-5">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={13} className="text-[#a78bfa]" />
          <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">// CIRCADIAN PROFILE</div>
        </div>
        <div className="font-mono text-xs text-zinc-500 mt-3">— no data —</div>
      </div>
    );
  }
  const peak = timeOfDay.peak_hour;
  const max = Math.max(1, ...(timeOfDay.hourly || []));
  return (
    <div data-testid="profile-timeofday" className="border border-[#a78bfa]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#a78bfa]/[0.10] to-[#a78bfa]/[0.02] p-5">
      <div className="flex items-center gap-2 mb-2">
        <Clock size={13} className="text-[#a78bfa]" />
        <div className="font-mono text-[10px] tracking-[0.25em] text-[#a78bfa]">// CIRCADIAN PROFILE</div>
      </div>
      <div className="font-heading text-white text-xl mt-2">
        <span className="text-[#a78bfa]">{HOUR_LABEL(peak)}</span> peak
      </div>
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a] mt-1">
        TAG: {timeOfDay.persona_tag}
      </div>
      {/* 24-hour sparkline */}
      <div className="mt-3 flex items-end gap-px h-10">
        {(timeOfDay.hourly || []).map((n, h) => (
          <div
            key={h}
            title={`${HOUR_LABEL(h)} · ${n}`}
            className="flex-1"
            style={{
              height: `${Math.max(2, (n / max) * 100)}%`,
              backgroundColor: h === peak ? "#a78bfa" : "#a78bfa40",
            }}
          />
        ))}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[9px] tracking-[0.2em] text-zinc-600">
        <span>12am</span>
        <span>6am</span>
        <span>12pm</span>
        <span>6pm</span>
        <span>11pm</span>
      </div>
    </div>
  );
}
